import providerDomains from './providerDomains.json';
import { FederatedProvider } from './federatedAuth';

/**
 * Consumer provider family a mailbox domain belongs to. Mirrors
 * `applications.federated_auth.domains.CONSUMER_PROVIDERS` on the backend;
 * `providerDomains.json` is generated by
 * `python manage.py dump_federated_provider_domains --write <this file>`.
 */
export type ProviderFamily = FederatedProvider;

const DOMAIN_TO_FAMILY: Record<string, ProviderFamily> = (() => {
  const map: Record<string, ProviderFamily> = {};
  (Object.keys(providerDomains) as ProviderFamily[]).forEach((family) => {
    (providerDomains[family] ?? []).forEach((domain) => {
      map[domain] = family;
    });
  });
  return map;
})();

/** Lowercased domain part of an email, or '' when there is no `@`. */
export const emailDomain = (email: string | null | undefined): string => {
  if (!email) {
    return '';
  }
  const at = email.lastIndexOf('@');
  if (at === -1) {
    return '';
  }
  return email
    .slice(at + 1)
    .trim()
    .toLowerCase();
};

/**
 * Detect the consumer provider family for an email address, or null.
 *
 * Pure and synchronous: evaluated on input when the value contains an `@` and
 * a `.` after it. Staff domains are intentionally not in the table (the staff
 * Workspace path is handled separately in the login form).
 */
export const detectEmailProvider = (
  email: string | null | undefined
): ProviderFamily | null => {
  const domain = emailDomain(email);
  if (!domain) {
    return null;
  }
  return DOMAIN_TO_FAMILY[domain] ?? null;
};

/** The provider domain table, exported for parity tests / debugging. */
export const PROVIDER_DOMAIN_TABLE = DOMAIN_TO_FAMILY;
